// Admin > Users page. Lists existing users (with role/status/last_login),
// supports inline role/status edits, and exposes a Create dialog that shows
// the freshly-generated temp password ONCE.
//
// Track 6.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { ApiError, users } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import type { UserListItem, UserRole, UserStatus } from '@frt/shared-types';

const ROLES: UserRole[] = ['admin', 'senior', 'user'];
const STATUSES: UserStatus[] = ['active', 'suspended'];

function formatDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return Number.isFinite(d.valueOf()) ? d.toLocaleString() : s;
}

export function AdminUsersPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const queryKey = ['admin', 'users'] as const;

  const [createOpen, setCreateOpen] = useState(false);
  const [tempPasswordReveal, setTempPasswordReveal] = useState<{
    username: string;
    temp_password: string;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => users.list({ per_page: 200 }),
    enabled: !!me && me.role === 'admin',
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof users.update>[1] }) =>
      users.update(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const suspend = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      users.suspend(id, reason ? { reason } : {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  if (!me || me.role !== 'admin') {
    return (
      <div className="text-sm text-slate-600">User management requires the admin role.</div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Users</h1>
        <Button onClick={() => setCreateOpen(true)}>New user</Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>All users</CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500">
                <tr>
                  <th className="py-1 pr-3">Username</th>
                  <th className="py-1 pr-3">Email</th>
                  <th className="py-1 pr-3">Role</th>
                  <th className="py-1 pr-3">Status</th>
                  <th className="py-1 pr-3">Last login</th>
                  <th className="py-1 pr-3">Reset?</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data?.data ?? []).map((u: UserListItem) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    isSelf={u.id === me.id}
                    onChangeRole={(role) => update.mutate({ id: u.id, patch: { role } })}
                    onChangeStatus={(status) =>
                      update.mutate({ id: u.id, patch: { status } })
                    }
                    onToggleReset={() =>
                      update.mutate({ id: u.id, patch: { must_reset: !u.must_reset } })
                    }
                    onSuspend={() => {
                      const reason = window.prompt('Suspension reason (optional):') ?? undefined;
                      suspend.mutate({ id: u.id, reason });
                    }}
                  />
                ))}
              </tbody>
            </table>
          )}
          {update.error && (
            <div className="text-sm text-red-600">
              {(update.error as Error).message}
            </div>
          )}
          {suspend.error && (
            <div className="text-sm text-red-600">
              {(suspend.error as Error).message}
            </div>
          )}
        </CardBody>
      </Card>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(res) => {
          setCreateOpen(false);
          setTempPasswordReveal({
            username: res.user.username,
            temp_password: res.temp_password,
          });
          queryClient.invalidateQueries({ queryKey });
        }}
      />

      <Dialog
        open={!!tempPasswordReveal}
        onClose={() => setTempPasswordReveal(null)}
        title="Temporary password"
        footer={
          <Button onClick={() => setTempPasswordReveal(null)}>I have saved it</Button>
        }
      >
        {tempPasswordReveal && (
          <div className="space-y-3">
            <p>
              User <span className="font-mono">{tempPasswordReveal.username}</span> was created.
              The one-time temporary password is shown below — it is{' '}
              <strong>not stored anywhere</strong> and will not appear again.
            </p>
            <div className="rounded bg-slate-100 px-3 py-2 font-mono text-base text-slate-900">
              {tempPasswordReveal.temp_password}
            </div>
            <p className="text-xs text-slate-500">
              The user must change this on first login.
            </p>
          </div>
        )}
      </Dialog>
    </div>
  );
}

function UserRow({
  user,
  isSelf,
  onChangeRole,
  onChangeStatus,
  onToggleReset,
  onSuspend,
}: {
  user: UserListItem;
  isSelf: boolean;
  onChangeRole: (role: UserRole) => void;
  onChangeStatus: (status: UserStatus) => void;
  onToggleReset: () => void;
  onSuspend: () => void;
}) {
  return (
    <tr className="border-t border-slate-100">
      <td className="py-1 pr-3 font-mono">
        {user.username}
        {isSelf && <span className="ml-2 text-xs text-slate-400">(you)</span>}
      </td>
      <td className="py-1 pr-3 text-slate-600">{user.email ?? '—'}</td>
      <td className="py-1 pr-3">
        <select
          value={user.role}
          onChange={(e) => onChangeRole(e.target.value as UserRole)}
          className="rounded border border-slate-300 bg-white px-1.5 py-1 text-sm"
          disabled={isSelf}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1 pr-3">
        <select
          value={user.status}
          onChange={(e) => onChangeStatus(e.target.value as UserStatus)}
          className="rounded border border-slate-300 bg-white px-1.5 py-1 text-sm"
          disabled={isSelf}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1 pr-3 text-slate-600">{formatDate(user.last_login_at)}</td>
      <td className="py-1 pr-3">
        <Button
          size="sm"
          variant={user.must_reset ? 'secondary' : 'ghost'}
          onClick={onToggleReset}
          disabled={isSelf}
        >
          {user.must_reset ? 'Required' : 'Force reset'}
        </Button>
      </td>
      <td className="py-1 text-right">
        {user.status === 'suspended' ? (
          <span className="text-xs text-slate-400">suspended</span>
        ) : (
          <Button
            size="sm"
            variant="destructive"
            onClick={onSuspend}
            disabled={isSelf}
          >
            Suspend
          </Button>
        )}
      </td>
    </tr>
  );
}

function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (res: Awaited<ReturnType<typeof users.create>>) => void;
}) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      users.create({
        username: username.trim(),
        email: email.trim() === '' ? null : email.trim(),
        role,
      }),
    onSuccess: (res) => {
      // Reset form before reveal so a re-open is clean.
      setUsername('');
      setEmail('');
      setRole('user');
      onCreated(res);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : String(err));
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New user"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (!username.trim()) {
                setError('username is required');
                return;
              }
              create.mutate();
            }}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <label className="block text-sm">
          <span className="text-slate-600">Username</span>
          <Input
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="alice"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">Email (optional)</span>
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="alice@example.com"
            type="email"
          />
        </label>
        <label className="block text-sm">
          <span className="text-slate-600">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
            className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <p className="text-xs text-slate-500">
          A 12-character temporary password is generated automatically and shown to you once
          after the user is created. The user must change it on first login.
        </p>
      </form>
    </Dialog>
  );
}
